<template>
  <div class="page-wrapper">
    <div v-if="isInitialLoading" class="initial-loading-overlay">
      <div class="initial-loading-spinner">
        <i class="fas fa-spinner fa-spin"></i>
        <div class="loading-text">Loading notes...</div>
      </div>
    </div>

    <div class="notes-page">
      <!-- MOTD Modal Component -->
      <MotdModal :route="$route.path" />
    </div>
    <div class="title-bar">
      <span style="flex: 1"
        >All Notes
        <span style="font-size: 0.8em; margin-left: 8px"
          >({{ filteredNotes.length }})</span
        ></span
      >
      <div class="button-container">
        <button class="back-button" @click="$router.go(-1)">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button class="to-top" @click="scrollToTop">
          <i class="fas fa-arrow-up"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
    </div>
    <div v-show="showNotesPanel" class="notes-panel" ref="notesPanel">
      <div class="search-container">
        <input
          ref="searchInputN"
          v-model="noteSearchQuery"
          placeholder="Search notes; use ! to exclude"
          class="search-input"
        />
        <button @click="clearNoteSearch()" class="clear-search-button">
          <i class="fas fa-times"></i>
        </button>
        <span class="search-buttons">
          <button
            @click="cycleDateFilter()"
            class="notes-search-button"
            :title="dateFilterTitle"
          >
            <i class="fas" :class="dateFilterIcon"></i>
          </button>
          <button
            @click="fetchNotes()"
            class="notes-search-button"
            :disabled="isRefreshing"
          >
            <i class="fas fa-refresh" :class="{ 'fa-spin': isRefreshing }"></i>
          </button>
          <button
            @click="toggleReadFilter()"
            class="notes-search-button"
            :title="showUnreadOnly ? 'Show All Notes' : 'Show Unread Only'"
          >
            <i
              class="fas"
              :class="showUnreadOnly ? 'fa-eye-slash' : 'fa-eye'"
            ></i>
          </button>
          <button
            @click="openAddNoteDialog()"
            class="notes-search-button"
            title="Add New Note"
          >
            <i class="fas fa-plus"></i>
          </button>
        </span>
      </div>

      <!-- Filter Status Display -->
      <div class="filter-status">
        <span v-if="dateFilter === 'today'">
          Showing: <strong>Today's notes</strong>
        </span>
        <span v-else-if="dateFilter === 'week'">
          Showing: <strong>This week's notes</strong>
        </span>
        <span v-else-if="dateFilter === 'month'">
          Showing: <strong>This month's notes</strong>
        </span>
        <span v-else-if="showUnreadOnly">
          Showing: <strong>Unread notes only</strong>
        </span>
        <span v-else> Showing: <strong>All notes</strong> </span>
      </div>

      <div class="notes-container-outer">
        <div class="notes-container" ref="notesContainer">
          <div v-if="filteredNotes.length === 0" class="no-notes-message">
            Click new to add a new note
          </div>
          <div
            v-else
            v-for="note in filteredNotes"
            :key="note.type + '.' + note.Id"
            class="note-card long-press-target"
            :class="{ 'note-read': isNoteRead(note) }"
            :data-note-key="notePhotoKey(note)"
            :data-has-photos="noteHasPhotos(note) ? '1' : undefined"
            @mousedown="startPress(note)"
            @mouseup="cancelPress(note)"
            @mouseleave="cancelPress(note)"
            @touchstart="startPress(note)"
            @touchend="cancelPress(note)"
            @click="handleNoteCardClick(note, $event)"
            @dblclick="handleNoteDoubleClick(note)"
          >
            <div class="note-header">
              <div
                class="note-title"
                v-html="highlightMatch(note.noteTitle, noteSearchQuery)"
              ></div>
              <div class="note-actions">
                <button
                  @click.stop="viewNote(note)"
                  class="note-action-btn"
                  title="View Note"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button
                  @click.stop="editNote(note)"
                  class="note-action-btn"
                  title="Edit Note"
                >
                  <i class="fas fa-pencil-alt"></i>
                </button>
                <button
                  @click.stop="shareNote(note)"
                  class="note-action-btn"
                  title="Share Note"
                >
                  <i class="fas fa-share-nodes"></i>
                </button>
                <button
                  @click.stop="toggleReadStatus(note)"
                  class="note-action-btn"
                  :title="isNoteRead(note) ? 'Mark as Unread' : 'Mark as Read'"
                >
                  <!-- eslint-disable-next-line prettier/prettier -->
                  <i
                    :class="
                      isNoteRead(note) ? 'fas fa-envelope' : 'fas fa-check'
                    "
                  ></i>
                </button>
                <button
                  @click.stop="softDeleteNote(note)"
                  class="note-action-btn"
                  title="Delete Note"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            <div
              class="note-body"
              v-html="
                highlightMatch(renderMarkDown(note.noteBody), noteSearchQuery)
              "
            ></div>
            <div v-if="noteHasPhotos(note)" class="note-card-photos">
              <img
                v-for="photo in notePhotosFor(note)"
                :key="photo.id"
                :src="photo.url"
                class="note-card-photo"
                alt="Note photo"
                @click.stop="openNotePhoto(photo, note)"
              />
              <div
                v-if="!notePhotosFor(note).length"
                class="note-card-photo-skel"
                aria-hidden="true"
              ></div>
            </div>

            <div class="note-footer">
              <span class="note-detail-section" :title="'Note ID: ' + note.Id"
                >{{ note.type }} : edited by {{ note.ModifiedBy }}
                {{ formatDateTime(note.ModifiedOn) }}</span
              >
              <div class="note-detail">{{ note.detail }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mark All as Read Button -->
      <div class="mark-all-read-container">
        <button @click="confirmMarkAllAsRead" class="mark-all-read-btn">
          Mark All as Read
        </button>
      </div>

      <!-- Note Dialog -->
      <div v-if="showNoteDialog" class="dialog-overlay">
        <div class="notes-dialog">
          <div class="notes-editor-header">
            <div>{{ editingNote ? 'Edit Note' : 'Add Note' }}</div>
          </div>
          <!-- Note Type Selection (for new and editing notes) -->
          <div class="note-type-selection">
            <label>Note Type:</label>
            <div class="note-type-buttons">
              <button
                type="button"
                class="note-type-btn"
                :class="{ active: selectedNoteType === 'property' }"
                @click="onNoteTypeChange('property')"
              >
                Property
              </button>
              <button
                type="button"
                class="note-type-btn"
                :class="{ active: selectedNoteType === 'tenant' }"
                @click="onNoteTypeChange('tenant')"
              >
                Tenant
              </button>
              <button
                type="button"
                class="note-type-btn"
                :class="{ active: selectedNoteType === 'lease' }"
                @click="onNoteTypeChange('lease')"
              >
                Lease
              </button>
              <button
                v-if="userRole < 3"
                type="button"
                class="note-type-btn"
                :class="{ active: selectedNoteType === 'office' }"
                @click="onNoteTypeChange('office')"
              >
                Office
              </button>
            </div>
          </div>

          <!-- Property Search (for property and lease notes) -->
          <div
            v-if="
              (selectedNoteType === 'property' ||
                selectedNoteType === 'lease') &&
              (!editingNote || editingNote.type !== selectedNoteType)
            "
            class="search-section"
          >
            <label>Select Property:</label>
            <div class="search-input-container">
              <input
                ref="propertySearchInput"
                type="text"
                v-model="propertySearchQuery"
                placeholder="Search properties..."
                class="search-input"
              />
              <div v-if="isSearchingProperties" class="search-loading">
                <i class="fas fa-spinner fa-spin"></i>
              </div>
            </div>
            <!-- Show suggestions until a property is chosen (new note or re-target while editing) -->
            <div class="search-results" v-if="!selectedProperty">
              <div
                v-for="property in filteredProperties"
                :key="property.id"
                @click="selectProperty(property)"
                class="search-result-item"
              >
                {{ property.display || property.fullAddress }}
              </div>
              <div
                v-if="
                  filteredProperties.length === 0 &&
                  propertySearchQuery.length >= 2
                "
                class="search-no-results"
              >
                No properties found
              </div>
            </div>
          </div>

          <!-- Tenant Search (for tenant notes) -->
          <div
            v-if="
              selectedNoteType === 'tenant' &&
              (!editingNote || editingNote.type !== selectedNoteType)
            "
            class="search-section"
          >
            <label>Select Tenant:</label>
            <div class="search-input-container">
              <input
                ref="tenantSearchInput"
                type="text"
                v-model="tenantSearchQuery"
                placeholder="Search tenants..."
                class="search-input"
              />
              <div v-if="isSearchingTenants" class="search-loading">
                <i class="fas fa-spinner fa-spin"></i>
              </div>
            </div>
            <div class="search-results" v-if="!selectedTenant">
              <div
                v-for="tenant in filteredTenants"
                :key="tenant.id"
                @click="selectTenant(tenant)"
                class="search-result-item"
              >
                {{ tenant.firstName || tenant.First }}
                {{ tenant.lastName || tenant.Last }}
              </div>
              <div
                v-if="
                  filteredTenants.length === 0 && tenantSearchQuery.length >= 2
                "
                class="search-no-results"
              >
                No tenants found
              </div>
            </div>
          </div>
          <!-- Hide title and body until property/tenant is selected -->
          <div v-if="!editingNote && canShowNoteInputs">
            <input
              ref="newInputN"
              type="text"
              v-model="newNoteTitle"
              placeholder="Note Title"
              class="note-title-input"
            />
            <div class="note-photo-toolbar">
              <button
                type="button"
                class="note-camera-btn"
                :disabled="!photoNoteTarget || notePhotoUploading"
                title="Save the note, then add photos"
                @click="triggerNotePhotoPicker"
              >
                <i
                  :class="
                    notePhotoUploading
                      ? 'fas fa-spinner fa-spin'
                      : 'fas fa-camera'
                  "
                ></i>
              </button>
            </div>
            <textarea
              v-model="newNoteBody"
              placeholder="Note Body"
              class="note-textarea"
            ></textarea>
            <a href="https://www.markdownguide.org/cheat-sheet/" target="_blank"
              >markdown help</a
            >
          </div>

          <!-- Show inputs for editing existing notes -->
          <div v-if="editingNote">
            <input
              ref="newInputN"
              type="text"
              v-model="newNoteTitle"
              placeholder="Note Title"
              class="note-title-input"
            />
            <div class="note-photo-toolbar">
              <button
                type="button"
                class="note-camera-btn"
                :disabled="!photoNoteTarget || notePhotoUploading"
                title="Add photo"
                @click="triggerNotePhotoPicker"
              >
                <i
                  :class="
                    notePhotoUploading
                      ? 'fas fa-spinner fa-spin'
                      : 'fas fa-camera'
                  "
                ></i>
              </button>
            </div>
            <textarea
              v-model="newNoteBody"
              placeholder="Note Body"
              class="note-textarea"
            ></textarea>
            <div
              v-if="notePhotosFor(editingNote).length"
              class="note-edit-photos"
            >
              <div
                v-for="photo in notePhotosFor(editingNote)"
                :key="photo.id"
                class="note-edit-photo-item"
              >
                <img
                  :src="photo.url"
                  class="note-card-photo"
                  alt="Note photo"
                  @click.stop="openNotePhoto(photo, editingNote)"
                />
                <button
                  type="button"
                  class="note-photo-delete-btn"
                  title="Delete photo"
                  @click.stop="askDeleteNotePhoto(photo, editingNote)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            <a href="https://www.markdownguide.org/cheat-sheet/" target="_blank"
              >markdown help</a
            >
          </div>
          <div class="dialog-buttons">
            <button
              v-if="editingNote"
              @click="toggleRawMode"
              :class="{ 'raw-active': rawMode }"
              class="raw-toggle-btn"
              title="Toggle visible line breaks"
            >
              Raw
            </button>
            <button @click="saveNote">Save</button>
            <span v-if="dialogSavedIndicator" class="saved-indicator"
              >(Saved)</span
            >
            <button @click="closeNoteDialog">Close</button>
            <button
              v-if="editingNote"
              @click="deleteNote"
              class="delete-button"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Note Popout Modal -->
      <div v-if="showPopout" class="popout-overlay" @click="closePopout">
        <div
          class="popout-content"
          :class="{ 'popout-expanded': popoutExpanded }"
          @click.stop
          @dblclick="editFromPopout"
        >
          <div class="popout-header">
            <div class="popout-title" v-if="!popoutEditMode">
              {{ popoutNote?.noteTitle || 'Untitled' }}
            </div>
            <div class="popout-title" v-if="popoutEditMode">
              Editing Note
              <transition name="fade">
                <span v-if="popoutSavedIndicator" class="saved-indicator"
                  >(Saved)</span
                >
              </transition>
            </div>
            <div class="popout-header-buttons">
              <button
                v-if="!popoutEditMode"
                @click.stop="togglePopoutWidth"
                class="popout-expand-btn"
                :title="popoutExpanded ? 'Collapse width' : 'Expand width'"
              >
                <i
                  :class="
                    popoutExpanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'
                  "
                ></i>
              </button>
              <button
                v-if="!popoutEditMode"
                @click="printNote"
                class="popout-print-btn"
                title="Print Note"
              >
                <i class="fas fa-print"></i>
              </button>
              <div v-if="popoutEditMode" class="font-size-controls">
                <button
                  @click="decreaseEditFontSize"
                  class="font-size-btn"
                  title="Decrease font size"
                >
                  <i class="fas fa-arrow-down"></i>
                </button>
                <button
                  @click="increaseEditFontSize"
                  class="font-size-btn"
                  title="Increase font size"
                >
                  <i class="fas fa-arrow-up"></i>
                </button>
              </div>
              <button
                v-if="popoutEditMode"
                @click="savePopoutNote"
                class="popout-save-btn"
              >
                <i class="fas fa-save"></i>
              </button>
              <button
                v-if="popoutEditMode"
                @click="cancelPopoutEdit"
                class="popout-cancel-btn"
              >
                <i class="fas fa-times"></i>
              </button>
              <button
                v-if="!popoutEditMode"
                @click="closePopout"
                class="popout-close-btn"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <div class="popout-body">
            <!-- Read Mode -->
            <div v-if="!popoutEditMode" class="popout-note-content">
              <div v-html="renderMarkDown(popoutNote?.noteBody)"></div>
              <div
                v-if="notePhotosFor(popoutNote).length"
                class="note-card-photos"
              >
                <img
                  v-for="photo in notePhotosFor(popoutNote)"
                  :key="photo.id"
                  :src="photo.url"
                  class="note-card-photo"
                  alt="Note photo"
                  @click.stop="openNotePhoto(photo, popoutNote)"
                />
              </div>
            </div>
            <!-- Edit Mode -->
            <div v-if="popoutEditMode" class="popout-edit-form">
              <input
                ref="popoutTitleInput"
                type="text"
                v-model="popoutEditTitle"
                placeholder="Note Title"
                class="popout-title-input"
              />
              <div class="note-photo-toolbar">
                <button
                  type="button"
                  class="note-camera-btn"
                  :disabled="!photoNoteTarget || notePhotoUploading"
                  title="Add photo"
                  @click="triggerNotePhotoPicker"
                >
                  <i
                    :class="
                      notePhotoUploading
                        ? 'fas fa-spinner fa-spin'
                        : 'fas fa-camera'
                    "
                  ></i>
                </button>
              </div>
              <textarea
                ref="popoutBodyTextarea"
                v-model="popoutEditBody"
                placeholder="Note Body"
                class="popout-body-textarea"
                :style="{ fontSize: popoutEditFontSize + 'em' }"
                @keydown="handlePopoutKeydown"
              ></textarea>
              <div
                v-if="notePhotosFor(popoutNote).length"
                class="note-edit-photos"
              >
                <div
                  v-for="photo in notePhotosFor(popoutNote)"
                  :key="photo.id"
                  class="note-edit-photo-item"
                >
                  <img
                    :src="photo.url"
                    class="note-card-photo"
                    alt="Note photo"
                    @click.stop="openNotePhoto(photo, popoutNote)"
                  />
                  <button
                    type="button"
                    class="note-photo-delete-btn"
                    title="Delete photo"
                    @click.stop="askDeleteNotePhoto(photo, popoutNote)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <a
                href="https://www.markdownguide.org/cheat-sheet/"
                target="_blank"
                class="popout-markdown-help"
                >markdown help</a
              >
            </div>
          </div>
          <div class="popout-footer">
            <div class="popout-meta">
              <span
                >{{ popoutNote?.type }} : edited by
                {{ popoutNote?.ModifiedBy }}</span
              >
              <span>{{ formatDateTime(popoutNote?.ModifiedOn) }}</span>
            </div>
            <div class="popout-hint" v-if="!popoutEditMode">
              Double-click to edit
            </div>
            <div class="popout-hint" v-if="popoutEditMode">
              Editing in popup mode
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Delete Note Confirmation Dialog -->
    <div v-if="showDeleteConfirm" class="delete-confirm-overlay">
      <div class="delete-confirm-dialog">
        <div class="delete-confirm-message">
          Are you sure you want to delete this note?
        </div>
        <div class="delete-confirm-buttons">
          <button
            @click="confirmDeleteNote"
            class="delete-confirm-btn delete-btn"
          >
            Delete
          </button>
          <button @click="showDeleteConfirm = false" class="delete-confirm-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
    <input
      ref="notePhotoInput"
      type="file"
      accept="image/*,image/heic,image/heif,.heic,.heif"
      multiple
      class="note-photo-file"
      @change="handleNotePhotoSelected"
    />
    <div
      v-if="fullSizeNotePhoto"
      class="note-photo-overlay"
      @click="fullSizeNotePhoto = null"
    >
      <img
        :src="fullSizeNotePhoto.url"
        class="note-photo-full"
        alt="Note photo"
      />
      <button
        type="button"
        class="note-photo-delete-btn note-photo-delete-btn-lg"
        title="Delete photo"
        @click.stop="
          askDeleteNotePhoto(fullSizeNotePhoto, fullSizeNotePhoto.note)
        "
      >
        <i class="fas fa-trash"></i>
      </button>
    </div>
    <div v-if="showDeletePhotoConfirm" class="delete-confirm-overlay">
      <div class="delete-confirm-dialog">
        <div class="delete-confirm-message">Delete this photo?</div>
        <div class="delete-confirm-buttons">
          <button
            @click="confirmDeleteNotePhoto"
            class="delete-confirm-btn delete-btn"
          >
            Delete
          </button>
          <button @click="cancelDeleteNotePhoto" class="delete-confirm-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import { renderNoteMarkdown } from '@/utils/noteMarkdown';
import { io } from 'socket.io-client'; // Import socket.io-client
import eventBus from './eventBus.js'; // Import eventBus
import MotdModal from './MotdModal.vue'; // Import MOTD Modal component
import QRCode from 'qrcode';
import {
  prepareImageForWorkOrderUpload,
  uploadFileLikeWorkOrder,
} from '@/utils/chunkedHeicUpload';

let socket;

export default {
  components: { MotdModal },
  data() {
    return {
      isInitialLoading: true, // Add loading state for initial mount
      isRefreshing: false,
      showNotesPanel: false,
      noteType: 'tenant',
      noteId: 0,
      editingNote: null,
      showNoteDialog: false,
      newNoteTitle: '',
      newNoteBody: '',
      noteSearchQuery: '',
      notes: [],
      dateFilter: 'all', // 'all', 'today', 'week'
      updateCheckInterval: null,
      pressDuration: 1500,
      pressTimeouts: {},
      userRole: 99, // Default to public role (most restricted)
      // New properties for enhanced functionality
      selectedNoteType: 'property',
      showUnreadOnly: false,
      readNotes: new Set(), // Track read notes locally
      dbInstance: null, // IndexedDB instance

      // Property search
      propertySearchQuery: '',
      selectedProperty: null,
      allProperties: [], // Store all properties for client-side filtering

      // Tenant search
      tenantSearchQuery: '',
      selectedTenant: null,
      allTenants: [], // Store all tenants for client-side filtering

      // Loading states
      isSearchingProperties: false,
      isSearchingTenants: false,

      // Popout functionality
      showPopout: false,
      popoutNote: null,
      popoutClickTimeout: null,
      clickTimer: null,
      popoutEditMode: false,
      popoutEditTitle: '',
      popoutEditBody: '',
      popoutEditFontSize: 0.75,
      popoutExpanded: false,
      popoutSavedIndicator: false,
      popoutSavedTimeout: null,
      dialogSavedIndicator: false,
      dialogSavedTimeout: null,
      rawMode: false,
      rawOriginalBody: '',
      showDeleteConfirm: false,
      pendingDeleteNote: null,
      notePhotosByKey: {},
      notePhotoLoadingKeys: {},
      notePhotoObserver: null,
      notePhotoUploading: false,
      fullSizeNotePhoto: null,
      showDeletePhotoConfirm: false,
      pendingDeletePhoto: null,
      pendingDeletePhotoNote: null,
    };
  },

  async mounted() {
    this.connectSocket();
    this.initializeIndexedDB();

    // Check if 'search' query parameter exists
    if (this.$route.query.search) {
      this.noteSearchQuery = this.$route.query.search;
    }

    // Hide read notes by default, unless searching by specific note ID
    const isIdSearch =
      this.$route.query.search && /^:\d+,/.test(this.$route.query.search);
    this.showUnreadOnly = !isIdSearch;

    this.showNotesPanel = true;

    // Fetch notes and wait for them to load
    await this.fetchNotes();
    this.fetchUserRole(); // Fetch user role on mount

    // Auto-open popout if URL search filters to a single note by ID
    if (this.$route.query.search && /^:\d+,/.test(this.$route.query.search)) {
      if (this.filteredNotes.length === 1) {
        this.showNotePopout(this.filteredNotes[0]);
      }
    }

    this.newNotesListener = (data) => {
      console.log('New notes received:', data[0]);
      if (!data || data.length === 0) return;

      // Process each received note
      data.forEach((newNote) => {
        // Find if the note already exists in this.notes
        const existingNoteIndex = this.notes.findIndex(
          (note) => note.Id === newNote.Id && note.type === newNote.type,
        );

        if (existingNoteIndex !== -1) {
          // Update existing note
          this.notes.splice(existingNoteIndex, 1, newNote);
        } else {
          // Add new note to the beginning of the array
          this.notes.unshift(newNote);
        }
      });
    };

    socket.on('newNotes', this.newNotesListener);

    // Load all properties and tenants once at startup
    this.loadAllProperties();
    this.loadAllTenants();

    // Add click listener for calendar date links
    document.addEventListener('click', this.handleCalendarLinkClick);

    // Turn off loading state after notes are loaded and rendered
    // Wait for Vue to finish DOM updates (including date links in renderMarkDown)
    await this.$nextTick();
    this.isInitialLoading = false;
    this.observeNotePhotoCards();

    // Log user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          axios.post('/api/log-location', {
            componentName: 'MobileNotes',
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          axios.post('/api/log-location', {
            componentName: 'MobileNotes',
            locationError: error.message,
          });
        },
      );
    }
  },

  beforeUnmount() {
    // Remove calendar link click listener
    document.removeEventListener('click', this.handleCalendarLinkClick);
    clearTimeout(this.pressTimeout);
    clearTimeout(this.clickTimer);
    socket.off('newNotes', this.newNotesListener);
    this.teardownNotePhotoObserver();
    Object.values(this.notePhotosByKey).forEach((photos) => {
      (photos || []).forEach((photo) => {
        if (photo && photo.url) URL.revokeObjectURL(photo.url);
      });
    });
  },

  computed: {
    photoNoteTarget() {
      if (this.popoutEditMode && this.popoutNote && this.popoutNote.Id) {
        return this.popoutNote;
      }
      if (this.editingNote && this.editingNote.Id) {
        return this.editingNote;
      }
      return null;
    },
    isMobile() {
      // Check for touch capability and mobile user agents
      const hasTouchScreen =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const mobileUserAgent =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );

      // Also check if we're on a mobile route (like /mnotes)
      const isMobileRoute =
        this.$route && this.$route.path && this.$route.path.startsWith('/m');

      // Consider it mobile if it has mobile user agent OR is on mobile route with touch capability
      return mobileUserAgent || (isMobileRoute && hasTouchScreen);
    },
    dateFilterIcon() {
      switch (this.dateFilter) {
        case 'today':
          return 'fa-calendar-day';
        case 'week':
          return 'fa-calendar-week';
        default:
          return 'fa-calendar';
      }
    },
    dateFilterTitle() {
      switch (this.dateFilter) {
        case 'today':
          return "Today's Notes";
        case 'week':
          return "This Week's Notes";
        default:
          return 'All Notes';
      }
    },
    filteredNotes() {
      let notes = this.filterNotes(this.noteSearchQuery);

      // Apply read/unread filter
      if (this.showUnreadOnly) {
        notes = notes.filter((note) => !this.isNoteRead(note));
      }

      // Apply date filter
      if (this.dateFilter === 'today') {
        const today = new Date();
        const todayStart = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

        notes = notes.filter((note) => {
          const noteDate = new Date(note.ModifiedOn);
          return noteDate >= todayStart && noteDate < todayEnd;
        });
      } else if (this.dateFilter === 'week') {
        const today = new Date();
        const weekStart = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - today.getDay(),
        );
        const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);

        notes = notes.filter((note) => {
          const noteDate = new Date(note.ModifiedOn);
          return noteDate >= weekStart && noteDate < weekEnd;
        });
      } else if (this.dateFilter === 'month') {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 1);

        notes = notes.filter((note) => {
          const noteDate = new Date(note.ModifiedOn);
          return noteDate >= monthStart && noteDate < monthEnd;
        });
      }

      return notes;
    },

    // Computed property for property search results (like LeaseSearch.vue)
    filteredProperties() {
      if (
        !this.allProperties ||
        !this.propertySearchQuery ||
        this.propertySearchQuery.length < 2
      ) {
        return [];
      }

      // Split searchQuery into tokens by comma and trim whitespace
      const tokens = this.propertySearchQuery
        .split(',')
        .map((token) => token.trim().toLowerCase());

      // If there are no tokens to filter by, return empty array
      if (tokens.length === 0) {
        return [];
      }

      // Filter properties that match all tokens
      return this.allProperties
        .filter((property) => {
          // Handle both fullAddress and display properties, with null checks
          const searchText = (
            property.display ||
            property.fullAddress ||
            ''
          ).toLowerCase();
          return tokens.every((token) => searchText.includes(token));
        })
        .slice(0, 10); // Limit to 10 results like LeaseSearch
    },

    // Computed property for tenant search results
    filteredTenants() {
      if (
        !this.allTenants ||
        !this.tenantSearchQuery ||
        this.tenantSearchQuery.length < 2
      ) {
        return [];
      }

      const query = this.tenantSearchQuery.toLowerCase();

      return this.allTenants
        .filter((tenant) => {
          // Defensive: allow for missing fields and fallback to empty string
          const firstName = tenant.firstName || tenant.First || '';
          const lastName = tenant.lastName || tenant.Last || '';
          const phone = tenant.phone || tenant.Phone || '';
          const email = tenant.email || tenant.Email || '';
          return (
            firstName.toLowerCase().includes(query) ||
            lastName.toLowerCase().includes(query) ||
            phone.toLowerCase().includes(query) ||
            email.toLowerCase().includes(query)
          );
        })
        .slice(0, 10); // Limit to 10 results
    },

    // Computed property to determine if search is active
    isSearchActive() {
      // Search is active if we're in new note mode and have search queries or results
      if (this.editingNote) return false;

      const hasPropertySearch =
        (this.selectedNoteType === 'property' ||
          this.selectedNoteType === 'lease') &&
        (this.propertySearchQuery.length > 0 ||
          this.filteredProperties.length > 0);

      const hasTenantSearch =
        this.selectedNoteType === 'tenant' &&
        (this.tenantSearchQuery.length > 0 || this.filteredTenants.length > 0);

      return hasPropertySearch || hasTenantSearch;
    },

    // Computed property to determine when to show note inputs
    canShowNoteInputs() {
      // Always show inputs for editing existing notes
      if (this.editingNote) return true;

      // For new notes, only show inputs after selection is made
      if (
        this.selectedNoteType === 'property' ||
        this.selectedNoteType === 'lease'
      ) {
        return !!this.selectedProperty;
      } else if (this.selectedNoteType === 'tenant') {
        return !!this.selectedTenant;
      } else if (this.selectedNoteType === 'office') {
        return true; // Office notes don't need a selection, show inputs immediately
      }

      return false;
    },
  },

  methods: {
    sendClose() {
      eventBus.emit('requestCloseMnotes');
    },
    // Function to get auth token from localStorage
    getAuthToken() {
      return localStorage.getItem('token');
    },

    // Function to fetch user role from API
    async fetchUserRole() {
      try {
        const response = await axios.get('/api/user-info');
        this.userRole = response.data.role;
      } catch (error) {
        console.error('Error fetching user role:', error);
        this.userRole = 99; // Default to most restricted on error
      }
    },

    // Function to connect to WebSocket server
    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        socket = io(socketUrl, {
          query: {
            token: token,
          },
        });

        socket.on('connect', () => {
          console.log('Connected to WebSocket server');
        });

        socket.on('disconnect', () => {
          console.log('Disconnected from WebSocket server');
        });
      } catch (error) {
        console.error('Error connecting to WebSocket server:', error);
      }
    },

    shareNote(note) {
      const noteId = note.Id;
      const noteType = note.type;

      const hostname = window.location.hostname;

      // Check if we're in development mode
      const isDev =
        process.env.NODE_ENV === 'development' ||
        hostname === 'localhost' ||
        hostname === 'pixiev2';

      // Always use production URL for sharing
      const noteUrl = `https://pixie.bigtapapp.com/mnotes?search=:${noteId},${noteType}`;

      // Build note details text
      let noteText = `📝 New note:\nFor: ${note.detail}\n${note.noteTitle}\n\n${noteUrl}`;

      console.log('shareNote - Environment check:', {
        NODE_ENV: process.env.NODE_ENV,
        hostname,
        isDev,
        noteUrl,
      });

      if (isDev) {
        console.log('Creating popup with note text:', noteText);

        // Show popup with selectable text in DEV
        const popupHtml = `
          <div style="background: white; padding: 20px; border-radius: 8px; max-width: 500px; max-height: 70vh; overflow-y: auto; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
              <h3 style="margin: 0; color: #333;">Note Details</h3>
              <button class="close-popup-btn" style="background: none; border: none; font-size: 24px; cursor: pointer; color: #999;">&times;</button>
            </div>
            <textarea readonly class="note-details-textarea" style="width: 92%; min-height: 200px; padding: 15px; font-family: monospace; font-size: 14px; border: 1px solid #ddd; border-radius: 4px; resize: vertical; cursor: pointer;" title="Click to select all">${noteText}</textarea>
            <button class="copy-details-btn" style="margin-top: 10px; padding: 10px 20px; background: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer; width: 100%;">Copy to Clipboard</button>
          </div>
        `;

        // Create a simple modal
        const overlay = document.createElement('div');
        overlay.className = 'swal2-container';
        overlay.style.cssText =
          'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 999999;';
        overlay.innerHTML = `<div class="swal2-popup">${popupHtml}</div>`;

        // Add event listeners after appending to DOM
        document.body.appendChild(overlay);

        // Textarea click to select
        const textarea = overlay.querySelector('.note-details-textarea');
        textarea.addEventListener('click', function () {
          this.select();
        });

        // Close button
        const closeBtn = overlay.querySelector('.close-popup-btn');
        closeBtn.addEventListener('click', () => {
          overlay.remove();
        });

        // Copy button
        const copyBtn = overlay.querySelector('.copy-details-btn');
        copyBtn.addEventListener('click', () => {
          // Use textarea selection and execCommand for non-HTTPS environments
          textarea.select();
          textarea.setSelectionRange(0, 99999); // For mobile devices

          try {
            const successful = document.execCommand('copy');
            if (successful) {
              overlay.remove();
            } else {
              alert('Failed to copy to clipboard');
            }
          } catch (err) {
            console.error('Copy failed:', err);
            alert('Failed to copy to clipboard');
          }
        });

        // Click outside to close
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            overlay.remove();
          }
        });

        console.log('Popup appended to body, overlay element:', overlay);
      } else {
        // Copy directly to clipboard in PRODUCTION
        navigator.clipboard
          .writeText(noteText)
          .then(() => {
            alert('Note details copied to clipboard!');
            console.log('Note details copied to clipboard:', noteText);
          })
          .catch((err) => {
            console.error('Failed to copy note details:', err);
            // Fallback method for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = noteText;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
              document.execCommand('copy');
              alert('Note details copied to clipboard!');
            } catch (fallbackErr) {
              console.error('Fallback copy failed:', fallbackErr);
              alert('Failed to copy to clipboard');
            }
            document.body.removeChild(textArea);
          });
      }
    },

    viewNote(note) {
      this.showNotePopout(note);
    },

    async softDeleteNote(note) {
      this.pendingDeleteNote = note;
      this.showDeleteConfirm = true;
    },

    async confirmDeleteNote() {
      const note = this.pendingDeleteNote;
      this.showDeleteConfirm = false;
      this.pendingDeleteNote = null;
      if (!note) return;

      try {
        const noteType = note.type;
        await axios.delete(`/api/note/${note.Id}`, {
          params: { noteType: noteType },
        });
        this.fetchNotes();
      } catch (error) {
        console.error('Error deleting note:', error);
        alert('Failed to delete note. Please try again.');
      }
    },

    scrollToTop() {
      const notesContainer = this.$refs.notesContainer;
      if (notesContainer) {
        notesContainer.scrollTo({
          top: 0,
          behavior: 'smooth', // Adds smooth scrolling effect
        });
      }
    },

    onShowNotesPanel() {
      /*
      this.$nextTick(() => {
        this.$refs.searchInputN.focus();
      });
      */
    },

    notePhotoKey(note) {
      if (!note || note.Id == null || !note.type) return '';
      return `${note.type}.${note.Id}`;
    },
    notePhotosFor(note) {
      const key = this.notePhotoKey(note);
      return (key && this.notePhotosByKey[key]) || [];
    },
    noteHasPhotos(note) {
      return (
        Number(note?.photoCount) > 0 || this.parseNotePhotoIds(note).length > 0
      );
    },
    notePhotosAlreadyLoaded(key) {
      return Object.prototype.hasOwnProperty.call(this.notePhotosByKey, key);
    },
    parseNotePhotoIds(note) {
      if (!note || !note.photoIds) return [];
      return String(note.photoIds)
        .split(',')
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    },
    ensureNotePhotoObserver() {
      if (this.notePhotoObserver) return true;
      const root = this.$refs.notesContainer;
      if (!root || typeof IntersectionObserver === 'undefined') return false;
      this.notePhotoObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const key = entry.target.getAttribute('data-note-key');
            this.notePhotoObserver.unobserve(entry.target);
            if (key) this.loadPhotosForNoteKey(key);
          }
        },
        {
          root,
          rootMargin: '400px 0px',
          threshold: 0,
        },
      );
      return true;
    },
    teardownNotePhotoObserver() {
      if (this.notePhotoObserver) {
        this.notePhotoObserver.disconnect();
        this.notePhotoObserver = null;
      }
    },
    observeNotePhotoCards() {
      const root = this.$refs.notesContainer;
      if (!root) return;
      if (typeof IntersectionObserver === 'undefined') {
        this.loadPhotosForVisibleNotes();
        return;
      }
      if (!this.ensureNotePhotoObserver()) return;
      const cards = this.$refs.notesContainer?.querySelectorAll(
        '.note-card[data-has-photos="1"]',
      );
      if (!cards) return;
      cards.forEach((el) => {
        const key = el.getAttribute('data-note-key');
        if (
          !key ||
          this.notePhotosAlreadyLoaded(key) ||
          this.notePhotoLoadingKeys[key]
        ) {
          return;
        }
        this.notePhotoObserver.observe(el);
      });
    },
    loadPhotosForNoteKey(key) {
      const note = (this.notes || []).find((n) => this.notePhotoKey(n) === key);
      if (note) this.loadNotePhotos(note);
    },
    async loadPhotosForVisibleNotes() {
      const notes = this.notes || [];
      await Promise.all(
        notes
          .filter((note) => this.noteHasPhotos(note))
          .map((note) => this.loadNotePhotos(note)),
      );
    },
    async loadNotePhotos(note, { force = false } = {}) {
      const key = this.notePhotoKey(note);
      if (!key) return;
      if (this.notePhotoLoadingKeys[key]) return;
      if (!force && this.notePhotosAlreadyLoaded(key)) return;
      this.notePhotoLoadingKeys = { ...this.notePhotoLoadingKeys, [key]: true };
      try {
        const response = await axios.get(
          `/api/notes/${note.type}/${note.Id}/photos`,
        );
        const photos = Array.isArray(response.data) ? response.data : [];
        const withUrls = await Promise.all(
          photos.map(async (photo) => {
            try {
              const imageResponse = await axios.get(
                `/api/note-photos/${photo.id}`,
                { responseType: 'blob' },
              );
              const blob = new Blob([imageResponse.data], {
                type: imageResponse.data.type || 'image/jpeg',
              });
              return { id: photo.id, url: URL.createObjectURL(blob) };
            } catch (err) {
              console.error(`Error fetching note photo ${photo.id}:`, err);
              return null;
            }
          }),
        );
        const existing = this.notePhotosByKey[key] || [];
        existing.forEach((p) => {
          if (p.url) URL.revokeObjectURL(p.url);
        });
        this.notePhotosByKey = {
          ...this.notePhotosByKey,
          [key]: withUrls.filter(Boolean),
        };
      } catch (error) {
        console.error('Error loading note photos:', error);
        if (!this.notePhotosAlreadyLoaded(key)) {
          this.notePhotosByKey = {
            ...this.notePhotosByKey,
            [key]: [],
          };
        }
      } finally {
        const next = { ...this.notePhotoLoadingKeys };
        delete next[key];
        this.notePhotoLoadingKeys = next;
      }
    },
    triggerNotePhotoPicker() {
      if (!this.photoNoteTarget || this.notePhotoUploading) return;
      const input = this.$refs.notePhotoInput;
      if (input) {
        input.value = '';
        input.click();
      }
    },
    openNotePhoto(photo, note) {
      this.fullSizeNotePhoto = { ...photo, note };
    },
    askDeleteNotePhoto(photo, note) {
      if (!photo || !photo.id) return;
      this.pendingDeletePhoto = photo;
      this.pendingDeletePhotoNote = note || photo.note || this.photoNoteTarget;
      this.showDeletePhotoConfirm = true;
    },
    cancelDeleteNotePhoto() {
      this.showDeletePhotoConfirm = false;
      this.pendingDeletePhoto = null;
      this.pendingDeletePhotoNote = null;
    },
    async confirmDeleteNotePhoto() {
      const photo = this.pendingDeletePhoto;
      const note = this.pendingDeletePhotoNote;
      this.cancelDeleteNotePhoto();
      if (!photo || !photo.id) return;
      try {
        await axios.delete(`/api/note-photos/${photo.id}`);
        const key = this.notePhotoKey(note);
        const current = this.notePhotosFor(note);
        const removed = current.find((p) => p.id === photo.id);
        if (removed && removed.url) URL.revokeObjectURL(removed.url);
        if (key) {
          this.notePhotosByKey = {
            ...this.notePhotosByKey,
            [key]: current.filter((p) => p.id !== photo.id),
          };
        }
        if (note) {
          const listed = this.notes.find(
            (n) => n.Id === note.Id && n.type === note.type,
          );
          if (listed) {
            listed.photoCount = this.notePhotosFor(note).length;
          }
        }
        if (this.fullSizeNotePhoto && this.fullSizeNotePhoto.id === photo.id) {
          this.fullSizeNotePhoto = null;
        }
      } catch (error) {
        console.error('Error deleting note photo:', error);
        alert('Failed to delete photo. Please try again.');
      }
    },
    async handleNotePhotoSelected(event) {
      const files = event.target.files;
      const note = this.photoNoteTarget;
      if (!files || !files.length || !note) return;
      this.notePhotoUploading = true;
      try {
        for (const original of files) {
          await this.uploadNotePhotoFile(note, original);
        }
        await this.loadNotePhotos(note, { force: true });
        const listed = this.notes.find(
          (n) => n.Id === note.Id && n.type === note.type,
        );
        if (listed) {
          listed.photoCount = this.notePhotosFor(note).length;
        }
      } catch (error) {
        console.error('Error uploading note photo:', error);
        alert('Failed to upload photo. Please try again.');
      } finally {
        this.notePhotoUploading = false;
        if (event.target) event.target.value = '';
      }
    },
    async uploadNotePhotoFile(note, fileToUpload) {
      const prepared = await prepareImageForWorkOrderUpload(fileToUpload);
      await uploadFileLikeWorkOrder(prepared.file, prepared.isHeic, {
        singleUrl: `/api/notes/${note.type}/${note.Id}/photos`,
        initUrl: '/api/note-photos/chunked/init',
        initBody: { noteType: note.type, noteId: note.Id },
        chunkUrl: '/api/note-photos/chunked/upload',
        finalizeUrl: '/api/note-photos/chunked/finalize',
      });
    },
    openNoteDialog(note = null) {
      this.editingNote = note;
      if (note) {
        this.newNoteTitle = note.noteTitle;
        this.newNoteBody = note.noteBody;
        // Set the selected note type to the current note's type
        this.selectedNoteType = note.type;
        this.loadNotePhotos(note);
      } else {
        this.newNoteTitle = '';
        this.newNoteBody = '';
      }
      this.showNoteDialog = true;
      this.$nextTick(() => {
        if (this.$refs.newInputN) {
          this.$refs.newInputN.focus();
        }
      });
    },

    closeNoteDialog() {
      // If raw mode is on, restore original text before closing
      if (this.rawMode) {
        this.newNoteBody = this.rawOriginalBody;
        this.rawMode = false;
      }
      this.showNoteDialog = false;
      // Reset search state
      this.propertySearchQuery = '';
      this.selectedProperty = null;
      this.tenantSearchQuery = '';
      this.selectedTenant = null;

      this.$nextTick(() => {
        if (this.$refs.searchInputN) {
          this.$refs.searchInputN.focus();
        }
      });
    },

    async fetchNotes(latestNote = null) {
      this.isRefreshing = true;
      await this.$nextTick();
      const spinnerStart = Date.now();
      console.log('fetchNotes: latestNote', latestNote);
      try {
        // Conditionally include the latestNote parameter in the request
        const response = await axios.get('/api/all-notes', {
          params: latestNote ? { latestNote } : {},
        });

        // If there are new notes, update the notes array
        if (response.data && response.data.length > 0) {
          if (latestNote) {
            this.notes = [...this.notes, ...response.data]; // Append new notes to the existing array
          } else {
            this.notes = response.data; // Overwrite if no latestNote is provided
          }
          this.latestNote = this.notes[0].ModifiedOn; // Update latestNote with the most recent modified date
        } else {
          // console.log('No new notes since the last fetch.');
        }
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        const elapsed = Date.now() - spinnerStart;
        if (elapsed < 1000) {
          await new Promise((r) => setTimeout(r, 1000 - elapsed));
        }
        this.isRefreshing = false;
        this.$nextTick(() => this.observeNotePhotoCards());
      }
    },

    toggleRawMode() {
      if (!this.rawMode) {
        // Entering raw mode: store original and show symbols
        this.rawOriginalBody = this.newNoteBody;
        this.newNoteBody = this.newNoteBody
          .replace(/\r\n/g, '\u00b6\n')
          .replace(/\n/g, '\u00b6\n')
          .replace(/\t/g, '\u2192\t');
        this.rawMode = true;
      } else {
        // Leaving raw mode: apply edits back by stripping symbols
        this.newNoteBody = this.newNoteBody
          .replace(/\u00b6\n/g, '\n')
          .replace(/\u00b6/g, '')
          .replace(/\u2192\t/g, '\t')
          .replace(/\u2192/g, '');
        this.rawMode = false;
      }
    },

    async saveNote() {
      // If raw mode is on, restore clean text before saving
      if (this.rawMode) {
        this.newNoteBody = this.newNoteBody
          .replace(/\u00b6\n/g, '\n')
          .replace(/\u00b6/g, '')
          .replace(/\u2192\t/g, '\t')
          .replace(/\u2192/g, '');
        this.rawMode = false;
      }
      const noteType = this.selectedNoteType; // Always use selected note type
      const isMovingNote =
        this.editingNote && this.editingNote.type !== noteType;

      // If moving note to different type, use POST; otherwise use PUT for edits
      const url =
        this.editingNote && !isMovingNote
          ? `/api/note/${this.editingNote.Id}`
          : '/api/note';
      const method = this.editingNote && !isMovingNote ? 'PUT' : 'POST';

      let noteId =
        this.editingNote && !isMovingNote ? this.editingNote.Id : this.noteId;
      let peopleId = undefined;

      // For new notes OR moved notes, determine the appropriate ID based on note type and selections
      if (!this.editingNote || isMovingNote) {
        if (noteType === 'property' && this.selectedProperty) {
          noteId = this.selectedProperty.id;
        } else if (noteType === 'tenant' && this.selectedTenant) {
          peopleId = this.selectedTenant.id;
        } else if (noteType === 'lease' && this.selectedProperty) {
          // Find current lease for the property
          try {
            const response = await axios.get(
              `/api/current-lease/${this.selectedProperty.id}`,
            );
            if (response.data && response.data.id) {
              noteId = response.data.id;
            } else {
              alert('No active lease found for this property');
              return;
            }
          } catch (error) {
            console.error('Error finding current lease:', error);
            if (error.response && error.response.status === 404) {
              alert('No active lease found for this property');
            } else {
              alert('Error finding active lease for this property');
            }
            return;
          }
        } else if (noteType === 'office') {
          // Office notes don't need any ID - they're not tied to any entity
          // Do nothing here, we'll handle this in the validation section
        } else {
          // Validation: ensure property/tenant is selected for new or moved notes
          if (noteType === 'property' || noteType === 'lease') {
            alert('Please select a property before saving the note');
            return;
          } else if (noteType === 'tenant') {
            alert('Please select a tenant before saving the note');
            return;
          } else if (noteType === 'office') {
            // Office notes are always valid - no selection needed
          }
        }
      }

      // If moving note to different type, delete from original table first
      if (isMovingNote) {
        try {
          await axios.delete(`/api/note/${this.editingNote.Id}`, {
            params: { noteType: this.editingNote.type, hardDelete: true },
          });
        } catch (error) {
          console.error('Error deleting original note:', error);
          alert('Failed to move note. Please try again.');
          return;
        }
      }

      // For tenant notes, only send peopleId; for office notes, send nothing; for others, send Id
      const noteData = {
        noteType: noteType,
        noteTitle: this.newNoteTitle,
        noteBody: this.newNoteBody,
      };
      if (noteType === 'tenant') {
        noteData.peopleId = peopleId;
      } else if (noteType === 'office') {
        // Office notes don't need any entity ID
      } else {
        noteData.Id = noteId;
      }

      return axios({
        method: method,
        url: url,
        data: noteData,
      })
        .then((response) => {
          if (this.editingNote && !isMovingNote) {
            // Defensive: check for response.data.note and its Id
            const updatedNote = response.data.note;
            if (updatedNote && updatedNote.Id !== undefined) {
              const index = this.notes.findIndex(
                (note) => note && note.Id === updatedNote.Id,
              );
              if (index !== -1) {
                this.notes.splice(index, 1, updatedNote);
              }
            }
          } else if (isMovingNote) {
            // Remove old note from local array when moving
            const oldIndex = this.notes.findIndex(
              (note) => note && note.Id === this.editingNote.Id,
            );
            if (oldIndex !== -1) {
              this.notes.splice(oldIndex, 1);
            }
            // Socket will add the new note
          } else {
            // Don't add the new note locally here - let the socket handler add it
            // to avoid duplicates. The socket 'newNotes' event will broadcast to all
            // clients including this one, and the newNotesListener will handle adding it.
            // Comment: This fixes the duplicate note issue mentioned at line 1089
          }
          // Show saved indicator instead of closing
          if (this.dialogSavedTimeout) {
            clearTimeout(this.dialogSavedTimeout);
          }
          this.dialogSavedIndicator = true;
          this.dialogSavedTimeout = setTimeout(() => {
            this.dialogSavedIndicator = false;
          }, 2000);
          // Update editingNote with saved data if it was a new note
          if (!this.editingNote && response.data.note) {
            this.editingNote = response.data.note;
          }
        })
        .catch((error) => {
          console.error('Error saving note:', error);
        });
    },

    handleCalendarLinkClick(event) {
      // Check if clicked element or its parent is a calendar link
      const link = event.target.closest('a[href^="#calendar-"]');
      if (!link) return;

      event.preventDefault();
      event.stopPropagation(); // Prevent click from bubbling to note preview

      // Parse the calendar link
      const href = link.getAttribute('href');
      console.log('📅 [MOBILENOTES] Clicked link href:', href);
      console.log('📅 [MOBILENOTES] Link text:', link.textContent);
      const match = href.match(/#calendar-(\d+)-(\d+)-(\d+)/);

      if (match) {
        const month = parseInt(match[1]);
        const year = parseInt(match[2]);
        const day = parseInt(match[3]);

        console.log('📅 [MOBILENOTES] Parsed from href:', {
          match,
          month,
          year,
          day,
        });

        console.log('📅 [MOBILENOTES] Opening calendar to:', {
          month,
          year,
          day,
        });

        // Emit event to open calendar to this specific date
        eventBus.emit('openCalendarToDate', {
          month,
          year,
          day,
          personal: false, // Open shared calendar by default
        });
      }
    },

    async deleteNote() {
      return;
      /*
      if (this.editingNote) {
        const noteType = this.noteType; // Use the noteType stored in the component

        axios
          .delete(`/api/note/${this.editingNote.Id}`, {
            params: { noteType: noteType }, // Include noteType as a query parameter
          })
          .then(() => {
            this.closeNoteDialog();
            this.fetchNotes();
          })
          .catch((error) => {
            console.error('Error deleting note:', error);
          });
      }
        */
    },

    filterNotes(query) {
      if (!query) return this.notes; // Return all notes if the query is empty

      // Check if query starts with ':' and a number
      const regex = /^:(\d+),\s*(.+)$/;
      const match = query.match(regex);

      if (match) {
        const noteId = match[1];
        let noteType;

        // Set noteType to '' if it's undefined or not a string, otherwise use .toLowerCase()
        if (typeof match[2] !== 'string') {
          noteType = '';
        } else {
          noteType = match[2].toLowerCase();
        }

        // Filter notes based on noteId and noteType
        return this.notes.filter(
          (note) =>
            note.Id.toString() === noteId && // Check if noteId matches
            note.type.toLowerCase() === noteType, // Check if noteType matches
        );
      } else {
        const tokens = query.split(',').map((token) => token.trim());
        let filteredNotes = this.notes;

        tokens.forEach((token) => {
          if (token.startsWith('!')) {
            const excludeQuery = token.slice(1).toLowerCase();
            filteredNotes = filteredNotes.filter(
              (note) =>
                !note.noteBody.toLowerCase().includes(excludeQuery) &&
                !note.noteTitle.toLowerCase().includes(excludeQuery) &&
                !(
                  note.detail &&
                  note.detail.toLowerCase().includes(excludeQuery)
                ),
            );
          } else {
            const includeQuery = token.toLowerCase();
            filteredNotes = filteredNotes.filter(
              (note) =>
                note.noteBody.toLowerCase().includes(includeQuery) ||
                note.noteTitle.toLowerCase().includes(includeQuery) ||
                (note.detail &&
                  note.detail.toLowerCase().includes(includeQuery)),
            );
          }
        });

        return filteredNotes;
      }
    },

    clearNoteSearch() {
      this.noteSearchQuery = '';
    },

    renderMarkDown(noteBody) {
      return renderNoteMarkdown(noteBody);
    },

    formatMarkdownTable(text) {
      if (!text) return text;

      const lines = text.split('\n');
      const formattedLines = [];
      let i = 0;

      while (i < lines.length) {
        const line = lines[i];

        // Check if this looks like a table line (contains pipes)
        if (line.trim().includes('|')) {
          // Collect all consecutive table lines
          const tableLines = [];
          let j = i;
          while (j < lines.length && lines[j].trim().includes('|')) {
            tableLines.push(lines[j]);
            j++;
          }

          if (tableLines.length >= 2) {
            // We have a potential table (at least header + separator)
            // Calculate max width for each column
            const rows = tableLines.map((line) =>
              line.split('|').map((cell) => cell.trim()),
            );

            // Find the maximum number of columns
            const maxCols = Math.max(...rows.map((row) => row.length));

            // Calculate max width for each column
            const columnWidths = [];
            for (let col = 0; col < maxCols; col++) {
              let maxWidth = 0;
              rows.forEach((row) => {
                if (row[col]) {
                  // Remove @ symbol and width markers for width calculation
                  let cellText = row[col].replace(/^\s*@\s*/, '');
                  cellText = cellText.replace(/<!--width:[^>]*-->/g, '');
                  maxWidth = Math.max(maxWidth, cellText.length);
                }
              });
              columnWidths.push(Math.max(maxWidth, 3)); // Minimum width of 3
            }

            // Format each row
            const formattedTable = rows.map((row) => {
              const paddedCells = row.map((cell, colIndex) => {
                if (colIndex === 0 || colIndex === row.length - 1) {
                  // First and last cells (usually empty for proper table format)
                  return cell;
                }

                const width = columnWidths[colIndex] || 3;

                // Check if this is a separator row (mostly dashes and colons, not content like phone numbers)
                const trimmedCell = cell.trim();
                const isSeparatorRow = /^:?-+:?$/.test(trimmedCell);

                if (isSeparatorRow) {
                  // Preserve alignment markers
                  const leftAlign = trimmedCell.startsWith(':');
                  const rightAlign = trimmedCell.endsWith(':');
                  const centerAlign = leftAlign && rightAlign;

                  let separator;
                  if (centerAlign) {
                    separator = ':' + '-'.repeat(width - 2) + ':';
                  } else if (leftAlign) {
                    separator = ':' + '-'.repeat(width - 1);
                  } else if (rightAlign) {
                    separator = '-'.repeat(width - 1) + ':';
                  } else {
                    separator = '-'.repeat(width);
                  }
                  return ' ' + separator + ' ';
                } else {
                  // Regular cell - pad with spaces
                  // Preserve @ symbol for centering
                  const hasAtSymbol = cell.trim().startsWith('@');
                  const cleanCell = cell.replace(/^\s*@\s*/, '');
                  const paddedCell = cleanCell.padEnd(width, ' ');
                  return ' ' + (hasAtSymbol ? '@ ' : '') + paddedCell + ' ';
                }
              });

              return paddedCells.join('|');
            });

            formattedLines.push(...formattedTable);
            i = j; // Skip past the table we just processed
          } else {
            // Not a complete table, just add the line
            formattedLines.push(line);
            i++;
          }
        } else {
          // Not a table line
          formattedLines.push(line);
          i++;
        }
      }

      return formattedLines.join('\n');
    },

    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    highlightMatch(text, query) {
      if (!text || !query) {
        return text;
      }

      const escapedQuery = this.escapeRegExp(query);
      const re = new RegExp(`(${escapedQuery})`, 'gi');
      if (text)
        return text.replace(
          re,
          (match) => `<mark class="highlight-match">${match}</mark>`,
        );
    },

    formatDateTime(dateString) {
      const options = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    startPress(note) {
      // Only handle long press on mobile
      if (!this.isMobile) {
        return;
      }

      // Store the unique timeout ID based on the note's ID or other unique attribute
      const noteKey = note.Id;

      // Set up the long-press timeout for editing
      this.pressTimeouts[noteKey] = setTimeout(() => {
        // Long press - open edit dialog
        this.openNoteDialog(note);
        // Clear the timeout after execution
        delete this.pressTimeouts[noteKey];
      }, this.pressDuration);
    },

    cancelPress(note) {
      // Only handle on mobile
      if (!this.isMobile) {
        return;
      }

      // Clear the specific timeout for this note to prevent accidental dialog open
      const noteKey = note.Id;
      if (this.pressTimeouts[noteKey]) {
        clearTimeout(this.pressTimeouts[noteKey]);
        delete this.pressTimeouts[noteKey]; // Clean up after clearing
      }
    },

    handleLongPress() {
      // Add any long press logic here, such as opening a menu or triggering an action
    },

    // ========== NEW ENHANCED FUNCTIONALITY METHODS ==========

    // IndexedDB Methods
    async initializeIndexedDB() {
      try {
        const request = indexedDB.open('NotesReadStatusDB', 1);

        request.onerror = () => {
          console.error('Error opening IndexedDB');
        };

        request.onsuccess = (event) => {
          this.dbInstance = event.target.result;
          this.loadReadStatus();
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('readNotes')) {
            const store = db.createObjectStore('readNotes', { keyPath: 'id' });
            store.createIndex('noteKey', 'noteKey', { unique: true });
          }
        };
      } catch (error) {
        console.error('Error initializing IndexedDB:', error);
      }
    },

    async loadReadStatus() {
      if (!this.dbInstance) return;

      try {
        const transaction = this.dbInstance.transaction(
          ['readNotes'],
          'readonly',
        );
        const store = transaction.objectStore('readNotes');
        const request = store.getAll();

        request.onsuccess = () => {
          this.readNotes.clear();
          request.result.forEach((item) => {
            this.readNotes.add(item.noteKey);
          });
        };
      } catch (error) {
        console.error('Error loading read status:', error);
      }
    },

    async saveReadStatus(noteKey, isRead) {
      if (!this.dbInstance) return;

      try {
        const transaction = this.dbInstance.transaction(
          ['readNotes'],
          'readwrite',
        );
        const store = transaction.objectStore('readNotes');

        if (isRead) {
          await store.put({
            id: noteKey,
            noteKey: noteKey,
            readAt: new Date().toISOString(),
          });
          this.readNotes.add(noteKey);
        } else {
          await store.delete(noteKey);
          this.readNotes.delete(noteKey);
        }
      } catch (error) {
        console.error('Error saving read status:', error);
      }
    },

    // Note Status Methods
    getNoteKey(note) {
      // Defensive: handle undefined/null or missing type/Id
      if (!note || typeof note !== 'object') return '';
      const type = note.type || '';
      const id = note.Id != null ? note.Id : '';
      return `${type}_${id}`;
    },

    isNoteRead(note) {
      return this.readNotes.has(this.getNoteKey(note));
    },

    async toggleReadStatus(note) {
      const noteKey = this.getNoteKey(note);
      const isCurrentlyRead = this.isNoteRead(note);
      await this.saveReadStatus(noteKey, !isCurrentlyRead);
    },

    // Filter Methods
    toggleReadFilter() {
      this.showUnreadOnly = !this.showUnreadOnly;
    },

    cycleDateFilter() {
      // Cycle through: all -> today -> week -> month -> all
      if (this.dateFilter === 'all') {
        this.dateFilter = 'today';
      } else if (this.dateFilter === 'today') {
        this.dateFilter = 'week';
      } else if (this.dateFilter === 'week') {
        this.dateFilter = 'month';
      } else {
        this.dateFilter = 'all';
      }
    },

    // Dialog Methods
    openAddNoteDialog() {
      this.editingNote = null;
      this.newNoteTitle = '';
      this.newNoteBody = '';
      this.selectedNoteType = 'office';
      this.showNoteDialog = true;
      this.$nextTick(() => {
        // Focus the appropriate input based on note type
        if (
          (this.selectedNoteType === 'property' ||
            this.selectedNoteType === 'lease') &&
          this.$refs.propertySearchInput
        ) {
          this.$refs.propertySearchInput.focus();
        } else if (
          this.selectedNoteType === 'tenant' &&
          this.$refs.tenantSearchInput
        ) {
          this.$refs.tenantSearchInput.focus();
        } else if (this.selectedNoteType === 'office' && this.$refs.newInputN) {
          this.$refs.newInputN.focus();
        }
      });
    },

    async editNote(note) {
      try {
        const response = await axios.get(`/api/note/${note.type}/${note.Id}`);
        const latest = response.data;
        // Update local note if server has a newer version
        if (new Date(latest.ModifiedOn) > new Date(note.ModifiedOn)) {
          note.noteTitle = latest.noteTitle;
          note.noteBody = latest.noteBody;
          note.ModifiedOn = latest.ModifiedOn;
          note.ModifiedBy = latest.ModifiedBy;
        }
      } catch (error) {
        console.error('Error checking for newer note version:', error);
      }
      this.editingNote = note;
      this.newNoteTitle = note.noteTitle;
      // Format tables in the note body before editing
      this.newNoteBody = this.formatMarkdownTable(note.noteBody);
      this.selectedNoteType = note.type;
      this.showNoteDialog = true;
      this.loadNotePhotos(note);
      this.$nextTick(() => {
        if (this.$refs.newInputN) {
          this.$refs.newInputN.focus();
        }
      });
    },

    // Mark All as Read Methods
    confirmMarkAllAsRead() {
      if (confirm('Are you sure you want to mark all notes as read?')) {
        this.markAllAsRead();
      }
    },

    async markAllAsRead() {
      try {
        // Mark all current notes as read
        for (const note of this.notes) {
          const noteKey = this.getNoteKey(note);
          await this.saveReadStatus(noteKey, true);
        }
        console.log('All notes marked as read');
      } catch (error) {
        console.error('Error marking all notes as read:', error);
      }
    },

    // Search Methods
    onNoteTypeChange(type) {
      this.selectedNoteType = type;
      // Clear search state when note type changes
      this.propertySearchQuery = '';
      this.selectedProperty = null;
      this.tenantSearchQuery = '';
      this.selectedTenant = null;
      // Ensure lists are loaded when opening property/tenant picker (edit re-target)
      if (
        (type === 'property' || type === 'lease') &&
        (!this.allProperties || this.allProperties.length === 0)
      ) {
        this.loadAllProperties();
      }
      if (
        type === 'tenant' &&
        (!this.allTenants || this.allTenants.length === 0)
      ) {
        this.loadAllTenants();
      }
      this.$nextTick(() => {
        // Use refs for reliable focus
        if (
          (type === 'property' || type === 'lease') &&
          this.$refs.propertySearchInput
        ) {
          this.$refs.propertySearchInput.focus();
        } else if (type === 'tenant' && this.$refs.tenantSearchInput) {
          this.$refs.tenantSearchInput.focus();
        } else if (type === 'office' && this.$refs.newInputN) {
          this.$refs.newInputN.focus();
        }
      });
    },

    // Load all properties and tenants once at startup
    async loadAllProperties() {
      this.isSearchingProperties = true;
      try {
        // Empty query → LIKE '%%' returns the full rentable list for client filter
        const response = await axios.get('/api/properties', {
          params: { query: '' },
        });
        this.allProperties = response.data || [];
      } catch (error) {
        console.error('Error loading all properties:', error);
        this.allProperties = [];
      } finally {
        this.isSearchingProperties = false;
      }
    },

    async loadAllTenants() {
      try {
        // Use the new API: /api/people (no leaseId)
        const response = await axios.get('/api/people');
        this.allTenants = response.data || [];
      } catch (error) {
        console.error('Error loading all tenants:', error);
        this.allTenants = [];
      }
    },

    selectProperty(property) {
      this.selectedProperty = property;
      this.propertySearchQuery = property.display || property.fullAddress;
      this.$nextTick(() => {
        if (this.$refs.newInputN) {
          this.$refs.newInputN.focus();
        }
      });
    },

    selectTenant(tenant) {
      this.selectedTenant = tenant;
      // Use both camelCase and PascalCase for compatibility
      const firstName = tenant.firstName || tenant.First || '';
      const lastName = tenant.lastName || tenant.Last || '';
      this.tenantSearchQuery = `${firstName} ${lastName}`.trim();
      this.$nextTick(() => {
        if (this.$refs.newInputN) {
          this.$refs.newInputN.focus();
        }
      });
    },

    // Utility function for debouncing
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // ========== POPOUT FUNCTIONALITY ==========
    handleNoteClick(note) {
      // On mobile, check if this is a quick tap (not a long press)
      if (this.isMobile) {
        const noteKey = note.Id;
        // If there's an active press timeout, this might be part of a long press
        // But we'll show the popout for now and cancel it if long press completes
        if (this.pressTimeouts[noteKey]) {
          // Short delay to see if this becomes a long press
          setTimeout(() => {
            // If the long press timeout is still active, don't show popout
            if (this.pressTimeouts[noteKey]) {
              return;
            }
            // Long press was cancelled, this was a short tap - show popout
            this.showNotePopout(note);
          }, 100);
          return;
        }
        // No press timeout, this is a simple tap
        this.showNotePopout(note);
        return;
      }

      // Desktop behavior: Use timeout to detect single vs double click
      if (this.clickTimer) {
        // This is a double click, cancel the single click
        clearTimeout(this.clickTimer);
        this.clickTimer = null;
        return;
      }

      this.clickTimer = setTimeout(() => {
        // This is a single click on desktop
        this.showNotePopout(note);
        this.clickTimer = null;
      }, 250);
    },

    handleNoteDoubleClick(note) {
      // Clear the single click timer if it exists
      if (this.clickTimer) {
        clearTimeout(this.clickTimer);
        this.clickTimer = null;
      }

      // On mobile, cancel any long-press timeout
      if (this.isMobile) {
        const noteKey = note.Id;
        if (this.pressTimeouts[noteKey]) {
          clearTimeout(this.pressTimeouts[noteKey]);
          delete this.pressTimeouts[noteKey];
        }
      }

      // Handle double click
      this.editNote(note);
    },

    showNotePopout(note) {
      // Simple implementation - just show the popout
      this.popoutNote = note;
      this.showPopout = true;
      if (note) this.loadNotePhotos(note);

      // Note: We don't mark as read here - only when user actually interacts with it
    },
    async printNote() {
      if (!this.popoutNote) return;

      const noteId = this.popoutNote.Id;
      const noteType = this.popoutNote.type;
      const noteUrl = `https://pixie.bigtapapp.com/mnotes?search=:${noteId},${noteType}`;

      // Generate QR code as data URL
      let qrDataUrl = '';
      try {
        qrDataUrl = await QRCode.toDataURL(noteUrl, {
          width: 100,
          margin: 1,
        });
      } catch (err) {
        console.error('Failed to generate QR code:', err);
      }

      const printWindow = window.open('', '_blank');
      const noteContent = this.renderMarkDown(this.popoutNote.noteBody);

      const qrHtml = qrDataUrl
        ? `<img src="${qrDataUrl}" style="width: 100px; height: 100px;" />`
        : '';

      printWindow.document.write(`
        <html>
          <head>
            <title>${this.popoutNote.noteTitle || 'Note'}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                margin: 0;
                padding: 0;
                color: #333;
              }
              @media print {
                body {
                  margin-top: 0.5in;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                  font-size: 100%;
                }
                @page {
                  margin-top: 0.5in;
                }
              }
              .print-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                border-bottom: 2px solid #3498db;
                padding-bottom: 5px;
                margin-bottom: 10px;
              }
              .print-header h1 {
                margin: 0;
                font-size: 24px;
                color: #2c3e50;
                border: none;
                padding: 0;
                flex: 1;
              }
              h1, h2, h3, h4, h5, h6 {
                color: #2c3e50;
                margin-top: 0;
                margin-bottom: 10px;
              }
              table {
                border-collapse: collapse;
                width: 100%;
                margin: 15px 0;
                font-size: 75%;
              }
              table th, table td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              table th {
                background-color: #f2f2f2;
                font-weight: bold;
              }
              .checkmark {
                color: #27ae60;
                font-weight: bold;
              }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <div class="print-header">
              <h1>${this.popoutNote.noteTitle || 'Untitled Note'}</h1>
              ${qrHtml}
            </div>
            <div>${noteContent}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
    },

    closePopout() {
      this.showPopout = false;
      this.popoutNote = null;
      this.popoutEditMode = false;
      this.popoutEditTitle = '';
      this.popoutEditBody = '';
    },

    editFromPopout() {
      // Don't enter edit mode if already editing
      if (this.popoutEditMode) {
        return;
      }

      // Enable edit mode in the popout
      this.popoutEditMode = true;
      this.popoutEditTitle = this.popoutNote?.noteTitle || '';
      // Format tables in the note body before editing
      this.popoutEditBody = this.formatMarkdownTable(
        this.popoutNote?.noteBody || '',
      );
      this.$nextTick(() => {
        if (this.$refs.popoutTitleInput) {
          this.$refs.popoutTitleInput.focus();
        }
      });
    },

    handlePopoutKeydown(event) {
      // CTRL+S to save without closing
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        this.savePopoutNoteQuiet();
      }
    },

    async savePopoutNoteQuiet() {
      // Save the note without closing edit mode
      try {
        const noteType = this.popoutNote.type;
        const url = `/api/note/${this.popoutNote.Id}`;
        const method = 'PUT';

        const noteData = {
          noteType: noteType,
          noteTitle: this.popoutEditTitle,
          noteBody: this.popoutEditBody,
          Id: this.popoutNote.Id,
        };

        const response = await axios({
          method: method,
          url: url,
          data: noteData,
        });

        if (response.data.note) {
          // Update the note in the notes array
          const updatedNote = response.data.note;
          const index = this.notes.findIndex(
            (note) => note && note.Id === updatedNote.Id,
          );
          if (index !== -1) {
            this.notes.splice(index, 1, updatedNote);
          }

          // Update the popout note but stay in edit mode
          this.popoutNote = updatedNote;

          // Re-run table formatter to re-align markdown table columns
          this.popoutEditBody = this.formatMarkdownTable(this.popoutEditBody);

          // Show saved indicator
          if (this.popoutSavedTimeout) {
            clearTimeout(this.popoutSavedTimeout);
          }
          this.popoutSavedIndicator = true;
          this.popoutSavedTimeout = setTimeout(() => {
            this.popoutSavedIndicator = false;
          }, 5000);
        }
      } catch (error) {
        console.error('Error saving note:', error);
      }
    },

    async savePopoutNote() {
      try {
        const noteType = this.popoutNote.type;
        const url = `/api/note/${this.popoutNote.Id}`;
        const method = 'PUT';

        const noteData = {
          noteType: noteType,
          noteTitle: this.popoutEditTitle,
          noteBody: this.popoutEditBody,
          Id: this.popoutNote.Id,
        };

        const response = await axios({
          method: method,
          url: url,
          data: noteData,
        });

        if (response.data.note) {
          // Update the note in the notes array
          const updatedNote = response.data.note;
          const index = this.notes.findIndex(
            (note) => note && note.Id === updatedNote.Id,
          );
          if (index !== -1) {
            this.notes.splice(index, 1, updatedNote);
          }

          // Update the popout note and exit edit mode
          this.popoutNote = updatedNote;
          this.popoutEditMode = false;
          this.popoutEditTitle = '';
          this.popoutEditBody = '';
        }
      } catch (error) {
        console.error('Error saving note:', error);
        // You might want to show an error message to the user here
      }
    },

    cancelPopoutEdit() {
      // Cancel edit mode and restore original content
      this.popoutEditMode = false;
      this.popoutEditTitle = '';
      this.popoutEditBody = '';
    },

    increaseEditFontSize() {
      if (this.popoutEditFontSize < 1.5) {
        this.popoutEditFontSize += 0.1;
      }
    },

    decreaseEditFontSize() {
      if (this.popoutEditFontSize > 0.5) {
        this.popoutEditFontSize -= 0.1;
      }
    },

    togglePopoutWidth() {
      this.popoutExpanded = !this.popoutExpanded;
    },

    handleNoteCardClick(note, event) {
      // Check if the click was on a button or other interactive element
      const target = event.target;
      const isButton = target.closest('button, .action-btn, .note-actions');
      const isCalendarLink = target.closest('a[href^="#calendar-"]');

      if (isButton || isCalendarLink) {
        return;
      }

      // For single click, show popout
      this.handleNoteClick(note);
    },

    async markNoteAsRead(note) {
      const noteKey = this.getNoteKey(note);
      if (!this.isNoteRead(note)) {
        await this.saveReadStatus(noteKey, true);
      }
    },

    // Note: toggleReadStatus is defined above in Note Status Methods section
  },

  watch: {
    showNotesPanel(newVal) {
      this.showNotesPanel = newVal; // Sync local data with prop
      if (newVal) {
        this.onShowNotesPanel();
      }
    },
    filteredNotes() {
      this.$nextTick(() => this.observeNotePhotoCards());
    },
  },
};
</script>

<style scoped>
.page-wrapper {
  background-color: #f0f0f0;
  max-width: 440px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

.initial-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 420px;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.initial-loading-spinner {
  text-align: center;
  color: #a080a8;
}

.initial-loading-spinner i {
  font-size: 3em;
  margin-bottom: 20px;
}

.initial-loading-spinner .loading-text {
  font-size: 1.2em;
  font-weight: 500;
  margin-top: 15px;
}

.title-bar {
  background: #6e5875;
  color: #fff;
  font-size: 1.4em;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.button-container {
  display: flex;
  align-items: center;
  gap: 5px;
}

.back-button,
.home,
.to-top {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.back-button {
  font-size: 0.7em;
}

.home,
.to-top {
  font-size: 0.6em;
}

/* Fix for FontAwesome icons in buttons */
.title-bar button i,
.notes-search-button i,
.note-header button i,
.dialog-buttons button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.search-input {
  width: 139px;
  padding: 7px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
}

input:focus {
  border: 1px solid #aaa;
  outline: none; /* Remove default outline */
  background-color: #f9f6f6; /* Optional: change background color on focus */
}

.edit-dialog-body .search-input {
  width: 170px !important;
  margin-left: 12px;
}
.note-title {
  font-weight: bold;
  margin-bottom: 5px;
}
.note-title-input {
  width: calc(100% - 20px);
  padding: 8px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
}

.notes-panel {
  border-radius: 8px;
  z-index: 9999;
  width: 94%;
  max-width: 400px;
  overflow: hidden;
  flex: 1;
  margin-left: 2%;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.search-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* margin-bottom: 15px; */
  margin-top: 15px;
  padding: 0 10px;
  max-width: 400px;
  flex-shrink: 0;
}

.search-input {
  width: 140px;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px 0 0 4px;
  border-right: none;
  font-size: 16px;
}

.clear-search-button {
  font-size: 0.7em;
  padding: 8px;
  border-radius: 0 4px 4px 0;
  border: 1px solid #aaa;
  background: #a080a8;
  cursor: pointer;
  transition: background-color 0.2s;
}

.clear-search-button:hover {
  background-color: #f0f0f0;
}

.search-buttons {
  display: flex;
  gap: 5px;
  margin-left: 10px;
}

.filter-status {
  height: 30px;
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  margin-left: 10px;
  font-size: 0.75em;
  color: #555;
}

.filter-status span {
  background: #ecdcf2;
  padding: 5px 12px;
  border-radius: 4px;
  border-left: 3px solid #a080a8;
  width: calc(100% - 37px);
}

.filter-status strong {
  color: #a080a8;
}

.notes-container-outer {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.notes-container {
  flex: 1;
  overflow-y: scroll;
  padding: 0 10px;
  max-width: 400px;
  /* Hide scrollbar for Chrome, Safari and Opera */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.notes-container::-webkit-scrollbar {
  display: none; /* Hide scrollbar for WebKit browsers */
}

.note-card {
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  padding: 10px;
  background-color: #ffffff;
  border: 1pt solid #ccc;
  border-radius: 5px;
  box-shadow: 3px 3px 5px 0px rgba(0, 0, 0, 0.2);
  margin-bottom: 10px;
  width: calc(100% - 20px);
  font-size: 0.8em;
  max-height: 30vh;
  display: flex;
  flex-direction: column;
}

.note-title-bar {
  display: flex;
  justify-content: flex-end;
}

.note-photo-file {
  display: none;
}

.note-photo-toolbar {
  display: flex;
  justify-content: flex-end;
  margin: 0 10px 8px 0;
}

.note-camera-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 6px;
  background: #a080a8;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.note-camera-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.note-card-photos {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 8px;
  max-height: 28vh;
  overflow-y: auto;
}

.note-card-photo {
  width: 100%;
  height: auto;
  max-height: 180px;
  object-fit: contain;
  border-radius: 4px;
  background: #f4f4f4;
  cursor: pointer;
}

.note-card-photo-skel {
  width: 100%;
  height: 120px;
  border-radius: 4px;
  background: linear-gradient(90deg, #ececec 0%, #f7f7f7 50%, #ececec 100%);
  background-size: 200% 100%;
  animation: note-photo-skel-shine 1.4s ease-in-out infinite;
}

@keyframes note-photo-skel-shine {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.note-edit-photos {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 10px 12px 0;
  max-height: 30vh;
  overflow-y: auto;
}

.note-edit-photo-item {
  position: relative;
}

.note-photo-delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.note-photo-delete-btn-lg {
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  font-size: 16px;
  z-index: 1;
}

.note-photo-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.note-photo-full {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.note-body {
  margin: 10px 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.note-body :deep(pre) {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

/* Table styles for rendered markdown in note-body */
.note-body :deep(table) {
  border-collapse: collapse;
  width: 97%;
  margin: 1em 0;
  table-layout: fixed; /* Makes columns have consistent width */
  font-size: 0.75em; /* Smaller font for tables */
}

.note-body :deep(th),
.note-body :deep(td) {
  border: 1px solid #e8e8e8; /* Light grey thin border */
  padding: 1px; /* Minimal padding all around */
  text-align: left;
  word-wrap: break-word; /* Handle long content in fixed-width columns */
  min-width: 80px; /* Ensure minimum width even for empty cells */
}

.note-body :deep(th) {
  background: #f5f5f5;
  font-weight: 600;
}

/* Support for center-aligned table columns */
.note-body :deep(th[style*='text-align: center']),
.note-body :deep(td[style*='text-align: center']),
.note-body :deep(th[align='center']),
.note-body :deep(td[align='center']) {
  text-align: center !important;
}

/* Add styling for the DONE checkmark */
:deep(.done-checkmark) {
  padding-right: 4px;
  font-size: 1em;
}

.note-footer {
  color: #888;
  text-align: right;
  font-size: 1em;
  font-style: italic;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #d5cfcf;
  padding-top: 6px;
}

.notes-header {
  color: #8d7294;
  font-weight: bold;
  font-size: 16pt;
  max-width: 400px;
  /* margin-top: 44px; */
  /* height: 40px; */
}

.note-button {
  margin-right: auto;
  width: 28px;
  height: 28px;
  font-size: 1em;
  background-color: #d5d5d5;
  border: 1pt solid #888;
  border-radius: 4px;
}

.note-detail-section {
  text-align: right;
  font-size: 0.9em;
}

.note-detail {
  font-size: 0.9em;
  font-weight: bold;
  color: #8c6197;
  font-style: normal;
}

.notes-search-button {
  font-size: 0.7em;
  margin-left: 3px;
  padding: 8px;
  border-radius: 5px;
  border: 1px solid #aaa;
}

/* for file upload dialog */
.notes-dialog {
  position: absolute;
  left: 35px;
  top: 75px;
  background: #eeeeee;
  padding: 20px;
  box-shadow: 14px 7px 10px rgba(0, 0, 0, 0.1);
  z-index: 1002;
  width: 297px;
  border-radius: 4px;
  border: 1px solid #888;
  max-height: 80vh;
  overflow-y: auto;
  /* Hide scrollbar for Chrome, Safari and Opera */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.notes-dialog::-webkit-scrollbar {
  display: none; /* Hide scrollbar for WebKit browsers */
}

.dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1000;
}

.notes-editor-header {
  user-select: none;
  background: #a080a8;
  color: #fff;
  margin-top: -20px;
  margin-bottom: 20px;
  padding: 10px;
  padding-left: 20px;
  margin-left: -20px;
  margin-right: -20px;
  border-radius: 4px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.notes-dialog input textarea {
  background: lightyellow !important;
}

.note-title-input {
  width: calc(100% - 20px);
  padding: 8px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
}

.note-textarea {
  width: calc(100% - 20px);
  height: 200px;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
}

.dialog-buttons button {
  margin-left: 5px;
  padding: 5px;
  width: 55px;
  border-radius: 5px;
  color: #ede7e7;
  background-color: #a080a8;
  border: 1pt solid #efecec;
}

.dialog-buttons {
  margin-top: 20px;
  text-align: right;
  border-radius: 4px;
  border-color: #ddd;
}

.dialog-buttons .delete-button {
  background-color: #89618f;
}

.dialog-buttons .raw-toggle-btn {
  background-color: #7a8a9a;
  font-size: 0.8em;
  width: 45px;
}

.dialog-buttons .raw-toggle-btn.raw-active {
  background-color: #d4a843;
  color: #333;
  font-weight: bold;
}

.notes-close-button {
  text-align: center;
  position: absolute;
  right: 36px;
  top: 5px;
  font-size: 0.8em;
  width: 25px;
}

:deep(.highlight-match) {
  background: #bb95c4;
}

.note-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.note-actions {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.note-card:hover .note-actions {
  opacity: 1;
}

/* Always show buttons on mobile/touch devices */
@media (max-width: 768px), (hover: none) {
  .note-actions {
    opacity: 1;
  }
}

.note-action-btn {
  width: 24px;
  height: 24px;
  background: #f5f5f5;
  border: 1px solid #d3d3d3;
  color: #808080;
  cursor: pointer;
  border-radius: 4px;
  padding: 2px;
  font-size: 11px;
  transition:
    background-color 0.2s,
    color 0.2s;
}

.note-action-btn:hover {
  background-color: #e8e8e8;
  color: #666;
}

.note-read {
  /* opacity: 0.7; */
  background-color: #d6d6d6;
  border: 1px solid #999;
}

.note-read .note-title {
  font-weight: normal;
}

.note-type-selection {
  margin-bottom: 15px;
}

.note-type-selection label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.note-type-buttons {
  display: flex;
  gap: 5px;
  width: 100%;
}

.note-type-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ccc;
  background: #f9f9f9;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0;
  width: auto;
  border-radius: 6px;
}

.note-type-btn:first-child {
  border-radius: 6px;
}

.note-type-btn:last-child {
  border-radius: 6px;
}

.note-type-btn:hover {
  background: #e9e9e9;
}

.note-type-btn.active {
  background: #a080a8;
  color: #fff;
  border-color: #907793;
}

.note-type-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
  font-size: 14px;
}

button {
  width: 36px;
  background: #a080a8;
  color: white;
  margin: 2px;
  border-radius: 5px;
  border: 1px solid #ddd;
}

.search-buttons {
  display: flex;
  gap: 3px;
  align-items: center;
}

.mark-all-read-container {
  padding: 10px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.mark-all-read-btn {
  width: 94%;
  padding: 6px;
  background: #cdcdcd;
  color: #878585;
  border: 1px solid #9b9b9b;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.mark-all-read-btn:hover {
  background: #907793;
}

/* Search component styles */
.search-section {
  margin-bottom: 15px;
  padding: 10px;
  background: #f8f8f8;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.search-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
  font-size: 14px;
}

.search-input-container {
  position: relative;
  margin-bottom: 10px;
}

.search-section .search-input {
  width: calc(100% - 16px);
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
}

.search-section .search-input:focus {
  border-color: #a080a8;
  outline: none;
  background-color: #fafafa;
}

.search-loading {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #a080a8;
}

.search-results {
  height: 200px; /* Fixed height to show about 8 result lines */
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  margin-bottom: 10px;
  /* Hide scrollbar for Chrome, Safari and Opera */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.search-results::-webkit-scrollbar {
  display: none; /* Hide scrollbar for WebKit browsers */
}

.search-result-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  transition: background-color 0.2s;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background: #f5f5f5;
}

.search-result-item.selected {
  background: #e8dce8;
  color: #333;
}

.search-no-results {
  padding: 16px 12px;
  text-align: center;
  color: #666;
  font-style: italic;
  font-size: 14px;
}

.selected-item {
  padding: 8px 12px;
  background: #e8f5e8;
  border: 1px solid #a8d8a8;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.selected-item strong {
  color: #2d7a2d;
}

/* Media query for desktop mode - adjust layout when component is in wrapper */
@media (min-width: 769px) {
  .page-wrapper {
    height: calc(790px + 15px); /* Match the wrapper height */
  }

  .notes-container-outer {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* Reduce height to ensure button fits */
    max-height: calc(100vh - 40px);
  }

  .mark-all-read-container {
    padding: 8px 10px;
    display: flex;
    justify-content: center;
    flex-shrink: 0;
  }
}

/* ========== POPOUT MODAL STYLES ========== */
.popout-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.popout-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 875px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px; /* Added 10px padding */
  transition: max-width 0.3s ease;
}

.popout-content.popout-expanded {
  max-width: 1312px; /* 875 * 1.5 = 1312.5 */
}

.popout-expand-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.1em;
  cursor: pointer;
  padding: 4px 8px;
  opacity: 0.9;
}

.popout-expand-btn:hover {
  opacity: 1;
}

.popout-header {
  background: #a080a8;
  color: #fff;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.popout-title {
  font-size: 1.2em;
  font-weight: bold;
  margin: 0;
  flex: 1;
}

.saved-indicator {
  font-size: 0.8em;
  font-weight: normal;
  color: #90ee90;
  margin-left: 8px;
}

.fade-enter-active {
  transition: opacity 0.3s ease;
}

.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.popout-close-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.2em;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.2s;
  width: auto;
  margin: 0;
}

.popout-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.popout-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;
  /* Allow text selection in popout */
  -webkit-user-select: text;
  -moz-user-select: text;
  user-select: text;
}

.popout-body::-webkit-scrollbar {
  width: 6px;
}

.popout-body::-webkit-scrollbar-track {
  background: transparent;
}

.popout-body::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.popout-body::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.popout-note-content {
  font-size: 0.9em;
  line-height: 1.6;
  color: #333;
  /* Enable text selection for all content */
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  user-select: text !important;
}

/* Ensure all child elements allow text selection */
.popout-note-content *,
.popout-note-content p,
.popout-note-content span,
.popout-note-content div {
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  user-select: text !important;
}

/* Style markdown content in popout */
.popout-note-content h1,
.popout-note-content h2,
.popout-note-content h3,
.popout-note-content h4,
.popout-note-content h5,
.popout-note-content h6 {
  color: #444;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

.popout-note-content h1 {
  font-size: 1.8em;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.3em;
}

.popout-note-content h2 {
  font-size: 1.5em;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.3em;
}

.popout-note-content p {
  margin-bottom: 1em;
}

.popout-note-content ul,
.popout-note-content ol {
  margin-bottom: 1em;
  padding-left: 2em;
}

.popout-note-content li {
  margin-bottom: 0.5em;
}

.popout-note-content blockquote {
  border-left: 4px solid #ddd;
  margin: 1em 0;
  padding: 0.5em 1em;
  background: #f9f9f9;
  color: #666;
}

.popout-note-content code {
  background: #f4f4f4;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
}

.popout-note-content pre {
  background: #f4f4f4;
  padding: 1em;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1em 0;
}

.popout-note-content pre code {
  background: transparent;
  padding: 0;
}

.popout-note-content :deep(table) {
  border-collapse: collapse;
  width: 95%;
  margin: 1em 0;
  table-layout: fixed; /* Makes columns have consistent width */
  font-size: 0.85em; /* Smaller font for tables */
}

.popout-note-content :deep(th),
.popout-note-content :deep(td) {
  border: 1px solid #e8e8e8; /* Light grey thin border */
  padding: 4px 16px; /* Reduced vertical padding for more compact rows */
  text-align: left;
  word-wrap: break-word; /* Handle long content in fixed-width columns */
  min-width: 80px; /* Ensure minimum width even for empty cells */
}

.popout-note-content :deep(th) {
  background: #f5f5f5;
  font-weight: bold;
}

/* Support for center-aligned table columns in popout */
.popout-note-content :deep(th[style*='text-align: center']),
.popout-note-content :deep(td[style*='text-align: center']),
.popout-note-content :deep(th[align='center']),
.popout-note-content :deep(td[align='center']) {
  text-align: center !important;
}

/* Also apply the same styling in popout content */
.popout-note-content :deep(.done-checkmark) {
  padding-right: 4px;
  font-size: 1em;
}

/* Popout edit form styles */
.popout-header-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.popout-save-btn,
.popout-cancel-btn,
.popout-print-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.1em;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  width: auto;
  margin: 0;
}

.popout-save-btn:hover {
  background: rgba(76, 175, 80, 0.3);
}

.popout-cancel-btn:hover {
  background: rgba(244, 67, 54, 0.3);
}

.popout-print-btn:hover {
  background: rgba(33, 150, 243, 0.3);
}

.popout-edit-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
  height: 100%;
}

.popout-title-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.8em;
  font-weight: bold;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  background: #fafafa;
  box-sizing: border-box;
}

.popout-title-input:focus {
  outline: none;
  border-color: #a080a8;
  background: #fff;
}

.font-size-controls {
  display: flex;
  gap: 4px;
  margin-right: 8px;
}

.font-size-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 0.9em;
  color: #fff;
}

.font-size-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.font-size-btn:active {
  background: rgba(255, 255, 255, 0.4);
}

.popout-body-textarea {
  flex: 1;
  width: 100%;
  min-height: 300px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  line-height: 1.4;
  background: #fafafa;
  resize: vertical;
  box-sizing: border-box;
}

.popout-body-textarea:focus {
  outline: none;
  border-color: #a080a8;
  background: #fff;
}

.popout-markdown-help {
  font-size: 0.9em;
  color: #666;
  text-decoration: none;
  align-self: flex-start;
}

.popout-markdown-help:hover {
  color: #a080a8;
  text-decoration: underline;
}

.popout-footer {
  background: #f8f9fa;
  padding: 12px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.popout-meta {
  font-size: 0.9em;
  color: #666;
  display: flex;
  gap: 15px;
}

.popout-hint {
  font-size: 0.85em;
  color: #999;
  font-style: italic;
}

/* Mobile responsiveness for popout */
@media (max-width: 768px) {
  .popout-overlay {
    padding: 10px;
  }

  .popout-content {
    max-width: 100%;
    max-height: 90vh;
  }

  .popout-header {
    padding: 12px 15px;
  }

  .popout-title {
    font-size: 1.1em;
  }

  .popout-body {
    padding: 15px;
  }

  .popout-note-content {
    font-size: 1em;
  }

  .popout-footer {
    padding: 10px 15px;
  }

  .popout-meta {
    flex-direction: column;
    gap: 5px;
  }
}

/* Share popup styles */
/* Calendar date link styling */
:deep(a[href^='#calendar-']) {
  color: #007bff;
  text-decoration: none;
  /* font-weight: 500; */
  border-radius: 3px;
  transition: all 0.2s ease;
  background: rgba(0, 123, 255, 0.08);
  font-size: 0.9em;
  /* border-left: 2px solid rgba(0, 123, 255, 0.3); */
  /* border-right: 2px solid rgba(0, 123, 255, 0.3); */
}

:deep(a[href^='#calendar-']:hover) {
  background: rgba(0, 123, 255, 0.15);
  border-bottom-color: rgba(0, 123, 255, 0.6);
  transform: translateY(-1px);
}

:deep(a[href^='#calendar-']:active) {
  transform: translateY(0);
  background: rgba(0, 123, 255, 0.2);
}

:deep(a.note-wo-chip) {
  color: #007bff;
  text-decoration: none;
  border: none;
  display: inline-block;
  padding: 2px 8px;
  margin: 0 1px;
  border-radius: 3px;
  transition: all 0.2s ease;
  background: rgba(0, 123, 255, 0.08);
  font-size: 1.2em;
  line-height: 1.3;
  vertical-align: baseline;
}

:deep(a.note-wo-chip:hover) {
  background: rgba(0, 123, 255, 0.15);
  border-bottom-color: rgba(0, 123, 255, 0.6);
  transform: translateY(-1px);
}

:deep(a.note-wo-chip:active) {
  transform: translateY(0);
  background: rgba(0, 123, 255, 0.2);
}

/* Phone number link styling */
:deep(a[href^='tel:']) {
  color: #28a745;
  text-decoration: none;
  border-radius: 3px;
  transition: all 0.2s ease;
  background: rgba(40, 167, 69, 0.08);
  font-size: 0.9em;
  padding: 1px 3px;
}

:deep(a[href^='tel:']:hover) {
  background: rgba(40, 167, 69, 0.15);
  text-decoration: underline;
  transform: translateY(-1px);
}

:deep(a[href^='tel:']:active) {
  transform: translateY(0);
  background: rgba(40, 167, 69, 0.2);
}

/* Delete Note Confirmation Dialog */
.delete-confirm-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.delete-confirm-dialog {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  padding: 20px 24px;
  min-width: 280px;
  text-align: center;
}

.delete-confirm-message {
  font-size: 1em;
  margin-bottom: 16px;
  color: #333;
}

.delete-confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.delete-confirm-btn {
  padding: 8px 24px;
  border: 1px solid #bbb;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  background: #e0e0e0;
  color: #333;
  white-space: nowrap;
  min-width: 80px;
  transition: background-color 0.2s ease;
}

.delete-confirm-btn:hover {
  background: #bdbdbd;
}
</style>
